import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { addYears, format, isPast } from 'date-fns'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import DatePicker from '../../../../../../components/DatePicker'
import FieldLabelWrapper from '../../../../../../features/components/Form/FieldLabelWrapper'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'

import { updateCampaign, clearUpdateCampaign } from '../../../../../../modules/actions/campaigns'
import {
  campaignUpdateErrorSelector,
  campaignUpdateIsLoadingSelector,
  campaignWasUpdatedSelector
} from '../../../../../../modules/selectors/campaigns'
import { selectedDV360AdAccountSelector } from '../../../../../../modules/selectors/adAccounts'

import { START_DATE } from '../../../../../ReusableFormFields/StartTimeFields/fields'
import { STOP_DATE } from '../../../../../ReusableFormFields/StopTimeFields/fields'
import { DATES_FORMAT_BE } from '../../../../../../constants/dates'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'
import { DV_360_PLATFORM } from '../../../../../../constants/selectLists/platformList'

import useStyles from '../../../../../../styles/common/drawerForms'

const DatesSectionForm = ({ editItemData: campaign, title, updateOptions, ...formProps }) => {
  const classes = useStyles()

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const campaignWasUpdated = useSelector(campaignWasUpdatedSelector)
  const { id: selectedAdAccountId } = useSelector(selectedDV360AdAccountSelector)

  const { [START_DATE]: startDate, [STOP_DATE]: endDate } = campaign

  const handleSubmit = useCallback(
    values => {
      const updateCampaignData = {
        account: selectedAdAccountId,
        // budget_lifetime must be included in payload (dv360 requires it when updating dates)
        budget_lifetime: campaign.budget_lifetime,
        [START_DATE]: format(values[START_DATE], DATES_FORMAT_BE),
        [STOP_DATE]: format(values[STOP_DATE], DATES_FORMAT_BE),
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        }
      }

      dispatch(updateCampaign(updateCampaignData, campaign.id, DV_360_PLATFORM, updateOptions))
    },
    [dispatch, campaign, title, selectedAdAccountId, updateOptions]
  )

  const initialValues = useMemo(
    () => ({
      [START_DATE]: (startDate && new Date(startDate)) || '',
      [STOP_DATE]: (endDate && new Date(endDate)) || ''
    }),
    [startDate, endDate]
  )

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit
  })

  const purifiedFormik = usePurifiedFormik(formik)

  const { setFieldValue, values } = purifiedFormik

  const isStartDateInPast = isPast(new Date(values[START_DATE]))

  const handleStartDatePickerChange = useCallback(
    value => {
      setFieldValue(START_DATE, value)
    },
    [setFieldValue]
  )

  const handleEndDatePickerChange = useCallback(
    value => {
      setFieldValue(STOP_DATE, value)
    },
    [setFieldValue]
  )

  const handleClearUpdateCampaign = useCallback(() => {
    dispatch(clearUpdateCampaign())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={campaignWasUpdated}
      clearEditItem={handleClearUpdateCampaign}
      errorSelector={campaignUpdateErrorSelector}
      isLoadingSelector={campaignUpdateIsLoadingSelector}
      {...formProps}
    >
      <FieldLabelWrapper label={t('Start Date')}>
        <DatePicker
          placeholder={t('Start Date')}
          className={classes.dateTimeInput}
          value={values[START_DATE]}
          changeHandler={handleStartDatePickerChange}
          disabled={isStartDateInPast}
          maxDate={addYears(new Date(), 1)}
        />
      </FieldLabelWrapper>

      <FieldLabelWrapper label={t('End Date')}>
        {values[STOP_DATE] ? (
          <DatePicker
            title={t('End Date')}
            className={classes.dateTimeInput}
            value={values[STOP_DATE]}
            changeHandler={handleEndDatePickerChange}
            minDate={isStartDateInPast ? new Date() : values[START_DATE]}
            maxDate={addYears(new Date(), 1)}
          />
        ) : (
          <>
            <p>{t('This campaign has been set up to run indefinitely until paused.')}</p>
          </>
        )}
      </FieldLabelWrapper>
    </EditForm>
  )
}

export default DatesSectionForm
